import type { Route } from './+types/terms'

export const meta = (): Route.MetaDescriptors => [
  { title: 'Terms | Vital Care Finder' },
  {
    name: 'description',
    content:
      'The Terms of Service governing your use of vit.al and the Vital Care Finder application inside AI assistants such as Claude and ChatGPT.',
  },
]

export const headers: Route.HeadersFunction = () => ({
  'Cache-Control': 'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400',
})

const privacyEmail = (
  <a className="underline" href="mailto:privacy@vit.al">
    privacy@vit.al
  </a>
)

const supportEmail = (
  <a className="underline" href="mailto:support@vit.al">
    support@vit.al
  </a>
)

export default function Terms() {
  return (
    <>
      <header className="space-y-3">
        <h1>Terms of Service</h1>
        <p>1 July 2026</p>

        <p>Vital Software Inc.</p>
      </header>

      <section>
        <h2>Agreement to these Terms</h2>
        <p>
          These Terms of Service ("Terms") are a binding agreement between you and Vital Software Inc. ("Vital", "we",
          "us", "our"). They govern your use of the website at{' '}
          <a className="underline" href="https://vit.al">
            https://vit.al
          </a>
          , and the "Vital Care Finder" application made available inside third-party AI assistants including
          Anthropic's Claude and OpenAI's ChatGPT (each a "Service", together the "Services").
        </p>
        <p>
          By accessing or using the Services, you agree to these Terms and to our Privacy Policy (which includes our
          Consumer Health Data Notice in Part Two), incorporated by reference. If you do not agree, do not use the
          Services.
        </p>
      </section>

      <section>
        <h2>What Vital provides</h2>
        <p>
          Vital helps consumers in the United States find and contact healthcare providers. The Services may include
          provider search, provider listing pages, provider comparisons, relative cost information, responsiveness
          information, patient-panel insights, insurance information, review summaries, callback requests, appointment
          request workflows, and Vital functionality surfaced inside AI assistants.
        </p>
        <p>
          Vital may use proprietary data, third-party data, provider data, user-provided information, and automated
          tools to produce provider matches, rankings, summaries, and explanations. We may change, add, suspend, or
          remove features at any time.
        </p>
      </section>

      <section>
        <h2>Vital is not a medical provider</h2>
        <p>
          Vital does not provide medical advice, diagnosis, or treatment. The Services are an information and navigation
          tool that helps you locate and contact providers and organise information you choose to share. The Services do
          not interpret your medical data to make a clinical diagnosis or to recommend a course of treatment, and they
          are not a substitute for the judgment of a licensed clinician.
        </p>
        <p>
          Any information, match, summary, or suggestion provided by the Services — including AI-generated content and
          any summary derived from a document you upload — is for general informational purposes only. Always seek the
          advice of a qualified health professional with any questions about a medical condition, and never disregard or
          delay seeking professional medical advice because of something you saw on the Services.
        </p>
        <p>
          <strong>
            In an emergency, call 911 or go to the nearest emergency room. Do not use the Services for medical
            emergencies.
          </strong>
        </p>
      </section>

      <section>
        <h2>Eligibility</h2>
        <p>
          You must be at least 18 years old and able to form a binding contract to use the Services. If you use the
          Services on behalf of another person, including a minor, you represent that you have legal authority to
          provide that person's information and to act on that person's behalf.
        </p>
      </section>

      <section>
        <h2>Accounts and access</h2>
        <p>
          Some features may be available without creating an account. If we offer account-based features, you are
          responsible for providing accurate information, keeping credentials confidential, and notifying us promptly of
          unauthorized access.
        </p>
      </section>

      <section>
        <h2>Using Vital through AI assistants and MCP-compatible platforms</h2>
        <p>
          When you use Vital Care Finder through ChatGPT, Claude, or another AI assistant or MCP-compatible platform,
          your use of that platform is also governed by that platform's own terms, privacy policy, and settings. Vital
          does not control those platforms, including their availability, security, user interface, model behavior, or
          how they process information outside the Vital service.
        </p>
        <p>
          Vital receives the information transmitted to us by the platform so we can perform the requested action, such
          as finding providers, returning provider information, or helping submit a callback request. Do not enter
          information into an AI assistant unless you are comfortable with that platform processing it under its own
          policies.
        </p>
      </section>

      <section>
        <h2>Provider information, ratings, and reviews</h2>
        <p>
          Provider information may include data from public sources, commercial datasets, insurance claims, provider
          directories, patient feedback, clinic responsiveness signals, and Vital's own analysis. Information may be
          incomplete, delayed, inaccurate, or out of date.
        </p>
        <p>
          Vital does not guarantee that a provider is accepting new patients, accepts your insurance, is in-network, has
          current availability, offers a specific service, or is suitable for your situation. You should verify all
          details directly with the provider and your insurer before relying on them.
        </p>
        <p>
          Ratings, review summaries, cost categories, care-focus indicators, responsiveness indicators, and
          patient-panel insights are informational signals. They are not a medical endorsement, credentialing
          determination, quality guarantee, or warranty of clinical outcome.
        </p>
      </section>

      <section>
        <h2>The Marketplace and callback requests</h2>
        <p>
          Vital helps you search for providers based on factors such as availability, insurance, location, specialty,
          and language. If you cannot get your recommended follow-up, we will help match you to a provider based on
          availability, insurance, location, specialty, and language.
        </p>
        <p>
          <strong>How matching works.</strong> Matches are based on availability, insurance, location, specialty, and
          language — not on payment to Vital. You are never charged for a match. Your diagnosis, insurance, and location
          information will be used solely to facilitate the match, as described in our Privacy Policy.
        </p>
        <p>
          <strong>No clinical endorsement.</strong> Vital is not recommending any matched provider on clinical grounds.
          A match reflects logistical fit, not an assessment of clinical quality. You should exercise your own judgment,
          verify the provider's credentials, and confirm suitability before booking.
        </p>
        <p>
          <strong>Your right to decline.</strong> You can always decline a match and seek your own provider. Using this
          feature is entirely voluntary.
        </p>
        <p>
          <strong>Submitting a request.</strong> If you request a callback or appointment, you authorise Vital to submit
          your request — including the contact information and any details you provide — to the relevant provider or
          clinic so they can contact you. Providers are independent third parties; Vital is not a party to, and is not
          responsible for, the care or any arrangement between you and a provider.
        </p>
        <p>
          <strong>Communications consent (telephone, SMS, and fax).</strong> By submitting a callback request or
          providing your phone number, you consent to receive calls, text messages, and related communications from
          Vital and/or the provider regarding your request, including via automated systems and prerecorded or
          AI-assisted voice. Message and data rates may apply. Consent to marketing communications is not a condition of
          using the Services, and you may opt out at any time (reply STOP to texts, or contact us at {supportEmail}).
        </p>
      </section>

      <section>
        <h2>Provider relationships</h2>
        <p>
          Vital may receive fees from providers or clinics in connection with the Marketplace and with placement in
          listings. Any such fee is a flat fee and is not a payment for, or contingent on, a specific patient referral
          or the volume or value of patients. This fee does not affect your cost of care, your copay, or what the
          provider charges you.
        </p>
        <p>
          <strong>Match ranking is not influenced by payment.</strong> Paid placement does not affect the ranking or
          ordering of provider matches. Where a listing or placement is paid, we will identify it as sponsored or
          advertising as required by law.
        </p>
      </section>

      <section>
        <h2>Your content and uploads</h2>
        <p>
          You retain ownership of documents, photos, and other materials you submit ("Your Content"). You grant Vital a
          limited, non-exclusive licence to host, process, and use Your Content solely to provide the Services to you.
          We process health information you upload as described in the Consumer Health Data Notice (Part Two) of our
          Privacy Policy, including our commitments not to sell it and to use only the minimum information necessary.
          You are responsible for ensuring you have the right to share any content you upload.
        </p>
      </section>

      <section>
        <h2>Acceptable use</h2>
        <ul>
          <li>Do not use the Services for unlawful, harmful, fraudulent, deceptive, or abusive purposes.</li>
          <li>Do not impersonate another person or misrepresent your authority to act for another person.</li>
          <li>Do not attempt to access, retrieve, or modify another person's information without authorization.</li>
          <li>
            Do not scrape, crawl, reverse engineer, overload, interfere with, or bypass security controls of the
            Services.
          </li>
          <li>
            Do not use Vital outputs to provide medical advice to third parties or to make clinical decisions without
            appropriate professional judgment.
          </li>
          <li>Do not submit malware, unlawful content, or content that violates another person's rights.</li>
        </ul>
      </section>

      <section>
        <h2>Intellectual property</h2>
        <p>
          The Services, including Vital's software, data compilations, analytics, text, and the Vital name and logos,
          are owned by Vital or its licensors and protected by law. We grant you a limited, revocable, non-transferable
          licence to use the Services for your personal, non-commercial use. All rights not expressly granted are
          reserved.
        </p>
      </section>

      <section>
        <h2>Disclaimers</h2>
        <p className="uppercase">
          The Services are provided "as is" and "as available". To the fullest extent permitted by law, Vital disclaims
          all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose,
          non-infringement, accuracy, availability, and any warranty regarding provider information, medical outcomes,
          or insurance coverage.
        </p>
      </section>

      <section>
        <h2>Limitation of liability</h2>
        <p className="uppercase">
          To the fullest extent permitted by law, Vital and its affiliates will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or for any loss arising from your reliance on
          provider information or your interactions with any provider. Vital's total aggregate liability for all claims
          is limited to US$100. Some jurisdictions do not allow certain limitations, so some of these may not apply to
          you.
        </p>
      </section>

      <section>
        <h2>Indemnification</h2>
        <p>
          You agree to indemnify and hold Vital harmless from claims arising out of your misuse of the Services, your
          violation of these Terms, or your violation of any law or third-party right, including in connection with
          content you upload.
        </p>
      </section>

      <section>
        <h2>Dispute resolution and arbitration; governing law</h2>
        <p>
          <strong>Arbitration and class-action waiver.</strong> Except for the carve-outs below, you and Vital agree
          that disputes will be resolved by binding individual arbitration administered by the American Arbitration
          Association under its Consumer Arbitration Rules, and you and Vital each waive the right to a jury trial and
          to participate in any class or representative proceeding. Carve-outs: small-claims actions, government-agency
          enforcement actions, injunctive relief in aid of arbitration, and intellectual-property claims may proceed in
          court.
        </p>
        <p>
          <strong>30-day opt-out.</strong> You may opt out of arbitration within 30 days of first accepting these Terms
          by writing to Vital Software Inc., Attention: Legal Department — Arbitration Opt-Out, 2803 Philadelphia Pike,
          Suite B, PMB 7017, Claymont, Delaware 19703.
        </p>
        <p>
          <strong>Governing law and venue.</strong> These Terms are governed by the laws of the State of Delaware,
          without regard to conflict-of-laws rules, and the state and federal courts located in Washington, D.C. have
          exclusive jurisdiction over any matter not subject to arbitration.
        </p>
      </section>

      <section>
        <h2>Changes to these Terms</h2>
        <p>
          We may update these Terms. Revisions are effective immediately. Continued use after changes take effect means
          you accept them.
        </p>
      </section>

      <section>
        <h2>Termination</h2>
        <p>
          We may suspend or end your access at any time. You may stop using the Services at any time. Sections that by
          their nature should survive (including disclaimers, limitation of liability, and dispute resolution) survive
          termination.
        </p>
      </section>

      <section>
        <h2>California residents; international use</h2>
        <p>
          California law permits users who are California residents to request and obtain from us once a year, free of
          charge, a list of the third parties to whom we have disclosed their personal information (if any) for their
          direct marketing purposes in the prior calendar year, as well as the type of personal information disclosed to
          those parties. If you are a California resident and would like to request this information, please submit your
          request in an email to {privacyEmail}.
        </p>
        <p>
          <strong>International use.</strong> The Services are intended for users located in the United States. We make
          no representation that the Services are appropriate or available for use outside the United States.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          Vital Software Inc.
          <br />
          Privacy: {privacyEmail}
        </p>
      </section>
    </>
  )
}
